import React, { useState } from 'react';
import { SalvBanner, SalvBannerType, SalvButton, SalvModal, SalvTagsInput } from '@salvapp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { showFeedback } from '../../feedback/feedbackReducer';
import { parseErrorMessage } from '../../common/errors/helpers';
import { useRerunScenariosForPersonsByIds } from './scenarioRerunActions';

interface Props {
  show: boolean;
  onClose: () => void;
}

interface FormValues {
  scenarioHandles: string[];
  personIds: string[];
}

const validationSchema = Yup.object().shape({
  scenarioHandles: Yup.array()
    .of(Yup.string().required())
    .min(1, 'At least one scenario handle is required')
    .required(),
  personIds: Yup.array()
    .of(Yup.string().required())
    .min(1, 'At least one person ID is required')
    .test(
      'all-numeric',
      'All person IDs must be valid numbers',
      (values) => !values || values.every((v) => !!v && !isNaN(Number(v)))
    )
    .required(),
});

const RerunByPersonIdsModal = ({ show, onClose }: Props) => {
  const dispatch = useDispatch();
  const mutation = useRerunScenariosForPersonsByIds();
  const [error, setError] = useState<string | null>(null);

  const initialValues: FormValues = { scenarioHandles: [], personIds: [] };

  const handleSubmit = async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
    setError(null);
    try {
      await mutation.mutateAsync({
        scenarioHandles: values.scenarioHandles,
        personIds: values.personIds.map(Number),
      });
      dispatch(showFeedback({ message: 'Scenario rerun for persons triggered successfully', success: true }));
      onClose();
      resetForm();
    } catch (e) {
      setError(await parseErrorMessage(e));
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} validateOnMount onSubmit={handleSubmit}>
      {({ values, errors, isValid, isSubmitting, setFieldValue, resetForm }) => (
        <SalvModal
          show={show}
          onHide={() => {
            resetForm();
            setError(null);
            onClose();
          }}
        >
          <Form>
            <SalvModal.Header>Rerun Scenarios for Persons by IDs</SalvModal.Header>
            <SalvModal.Body>
              {error && <SalvBanner message={error} type={SalvBannerType.DANGER} clearable className='m-b-16' />}
              <SalvTagsInput
                value={values.scenarioHandles}
                onChange={(newValues) => setFieldValue('scenarioHandles', newValues)}
                label='Scenario Handles'
                placeholder='Enter scenario handle'
                isInvalid={!!errors.scenarioHandles}
                className='m-b-16'
              />
              <SalvTagsInput
                value={values.personIds}
                onChange={(newValues) => setFieldValue('personIds', newValues)}
                label='Person IDs'
                placeholder='Enter person ID'
                isInvalid={!!errors.personIds}
              />
              {errors.personIds && typeof errors.personIds === 'string' && (
                <div className='salv-error-message'>{errors.personIds}</div>
              )}
            </SalvModal.Body>
            <SalvModal.Footer>
              <SalvButton
                variant='muted'
                onClick={() => {
                  resetForm();
                  setError(null);
                  onClose();
                }}
                label='Cancel'
              />
              <SalvButton label='Rerun' type='submit' disabled={!isValid || isSubmitting || mutation.isPending} />
            </SalvModal.Footer>
          </Form>
        </SalvModal>
      )}
    </Formik>
  );
};

export default RerunByPersonIdsModal;
